<!--
  Ordini Parziali — lista ordini parziali in lavorazione.
  Redesigned 2026-06-15 (v2.8.0, Phase 1.5 Chunk E.1).

  Drops kendo-grid + kendo-datepicker (era nel container) + mat-spinner
  + ~40 inline `style="..."` (31 kendo refs → 0) + legenda accordion
  (sostituita da chip viola persistente, brand color $partial=#8700f5).

  Pattern Diakonix coerente con storico-parziali (G.1):
    - Sticky header con titolo + chip "Parziale" + counter
    - Filter bar nativa (search + date range)
    - Table-card Diakonix con padding 14×16
    - Per-row actions: ✏️ Assegna (apri modale E.2) + 🔒 Blocca/Sblocca
    - Click row → openAssegna detail modal (E.2)
-->

<div class="parziali-page">
  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="parziali-header">
    <div class="parziali-header__titles">
      <h1 class="parziali-header__title">
        ⏳ Ordini parziali
        <span
          class="status-chip status-chip--partial"
          title="Tutti gli ordini in questa lista hanno consegna parziale"
        >
          Parziale
        </span>
      </h1>
      <p class="parziali-header__subtitle">
        @if (displayData.length > 0) {
          <strong>{{ displayData.length }}</strong>
          {{
            displayData.length === 1 ? "ordine parziale" : "ordini parziali"
          }}
          @if (filterTerm) {
            · filtro <strong>"{{ filterTerm }}"</strong>
          }
        } @else if (loading) {
          Caricamento ordini parziali…
        } @else {
          Nessun ordine parziale in lavorazione
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="getOrdineParziali()"
      title="Aggiorna lista"
      aria-label="Aggiorna lista"
    >
      🔁
    </button>
  </header>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="parziali-filters" aria-label="Filtri ricerca">
    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchParziali"
        >Cerca ordine</label
      >
      <div class="filter-field__input-wrap">
        <input
          id="searchParziali"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (ngModelChange)="handleFilterChange()"
          (keyup.enter)="searchOrders()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >
            ×
          </button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date: 'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date: 'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >
      🔍 Cerca
    </button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="parziali-table-card" aria-label="Elenco ordini parziali">
    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (displayData.length === 0) {
      <div class="parziali-empty">
        <div class="parziali-empty__icon" aria-hidden="true">
          @if (filterTerm) {
            🔎
          } @else {
            📭
          }
        </div>
        <p class="parziali-empty__msg">
          @if (filterTerm) {
            Nessun ordine parziale corrisponde a
            <strong>"{{ filterTerm }}"</strong>.
          } @else {
            Nessun ordine parziale in lavorazione al momento.
          }
        </p>
      </div>
    } @else {
      <div class="parziali-table-scroll">
        <table class="parziali-table" role="table">
          <thead>
            <tr>
              <th class="col-order sortable" (click)="sort('or_order_number')">
                N° Ordine
                <span class="sort-icon">{{ sortIcon("or_order_number") }}</span>
              </th>
              <th class="col-customer sortable" (click)="sort('or_customer')">
                Cliente
                <span class="sort-icon">{{ sortIcon("or_customer") }}</span>
              </th>
              <th class="col-num sortable" (click)="sort('or_articles_number')">
                N° Articoli
                <span class="sort-icon">{{
                  sortIcon("or_articles_number")
                }}</span>
              </th>
              <th class="col-date sortable" (click)="sort('or_order_date')">
                Data ordine
                <span class="sort-icon">{{ sortIcon("or_order_date") }}</span>
              </th>
              <th class="col-date">Ultima spedizione</th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of displayData | sortBy: sortField : sortDir;
              track item.order_id + "-" + $index
            ) {
              <tr
                class="parziali-row"
                [class.parziali-row--blocked]="isBlocked(item)"
                (click)="gotoDetails(item.or_gamma_id)"
                title="Assegna ordine parziale"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{
                    item.or_order_number || "—"
                  }}</span>
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{
                    item.or_customer || "—"
                  }}</span>
                </td>
                <td class="col-num">
                  <span class="cell-num">{{
                    item.or_articles_number ?? 0
                  }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{
                      item.or_order_date
                        ? (item.or_order_date | date: "dd/MM/yyyy")
                        : "—"
                    }}
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{
                      item.or_last_delivery_date
                        ? (item.or_last_delivery_date | date: "dd/MM/yyyy")
                        : "—"
                    }}
                  </span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--primary"
                      title="Assegna ordine"
                      (click)="gotoDetails(item.or_gamma_id)"
                    >
                      ✏️
                    </button>

                    @if (isBlocked(item)) {
                      <button
                        type="button"
                        class="icon-btn icon-btn--blocked"
                        title="Ordine bloccato — clicca per sbloccare"
                        aria-label="Sblocca ordine"
                        (click)="unblock(item)"
                      >
                        🛑
                      </button>
                    } @else {
                      <button
                        type="button"
                        class="icon-btn icon-btn--unblocked"
                        title="Ordine attivo — clicca per bloccare"
                        aria-label="Blocca ordine"
                        (click)="block(item)"
                      >
                        🔓
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>
</div>

<!-- ─── Modale dettagli (Chunk E.2 — restyle in arrivo) ─────────── -->
@if (modalService.isAssegnaDet$ | async) {
  <app-ordini-parziali-dettagli
    [isAssegnaOn]="modalService.isAssegnaDet"
    [orderId]="selectedOrderId"
    (result)="onChildClose($event)"
  ></app-ordini-parziali-dettagli>
}
